import { ComparisonOperator, TemplatePresentationItemDisplay } from 'types/dataHelios';
import type { HeliosRecordDataTablesColumn, LogicalOperator } from 'types/dataHelios';

const { Equals, NotEquals, GreaterThan, GreaterThanOrEqual, LessThan, LessThanOrEqual, Between, Empty, NotEmpty, Contains, NotContains } =
  ComparisonOperator;

export const DEFAULT_OPERATORS = [Equals, NotEquals, Empty, NotEmpty] as const;

export const OPERATORS_BY_TYPE: Record<string, readonly ComparisonOperator[]> = {
  Int32: [Equals, NotEquals, GreaterThan, GreaterThanOrEqual, LessThan, LessThanOrEqual, Between, Empty, NotEmpty],
  Int64: [Equals, NotEquals, GreaterThan, GreaterThanOrEqual, LessThan, LessThanOrEqual, Between, Empty, NotEmpty],
  Decimal: [Equals, NotEquals, GreaterThan, GreaterThanOrEqual, LessThan, LessThanOrEqual, Between, Empty, NotEmpty],
  String: [Equals, NotEquals, Contains, NotContains, Empty, NotEmpty],
  DateTime: [Equals, NotEquals, GreaterThan, GreaterThanOrEqual, LessThan, LessThanOrEqual, Between, Empty, NotEmpty],
  Boolean: [Equals, NotEquals],
};

export const STATIC_OPERATORS = [Equals, NotEquals] as const;

const isStaticLookup = (displayType: TemplatePresentationItemDisplay): boolean =>
  displayType === TemplatePresentationItemDisplay.Browse || displayType === TemplatePresentationItemDisplay.StaticSelection;

export const toStringValue = (value: unknown): string => (typeof value === 'string' ? value : '');

export const getOperatorsForDataType = (dataType: string, displayType: TemplatePresentationItemDisplay): readonly ComparisonOperator[] =>
  isStaticLookup(displayType) && dataType === 'String' ? STATIC_OPERATORS : OPERATORS_BY_TYPE[dataType] || DEFAULT_OPERATORS;

const NUMERIC_TYPES: readonly string[] = ['Int32', 'Int64', 'Decimal'];

const INPUT_TYPE: Record<string, 'text' | 'number' | 'date'> = {
  Int32: 'number',
  Int64: 'number',
  Decimal: 'number',
  DateTime: 'date',
};

export const getValueInputType = (dataType: string, displayType: TemplatePresentationItemDisplay): 'text' | 'number' | 'date' =>
  displayType === TemplatePresentationItemDisplay.Browse ? 'text' : INPUT_TYPE[dataType] || 'text';

export const isNumericDataType = (dataType: string): boolean => NUMERIC_TYPES.includes(dataType);

export const isDateDataType = (dataType: string): boolean => dataType === 'DateTime';

export const BLANK_OPERATORS: readonly ComparisonOperator[] = [Empty, NotEmpty];

export const isEmpty = (v: unknown): boolean => v === '' || v === undefined || v === null || (Array.isArray(v) && v.length === 0);

export interface FilterFormValues {
  columnKey: string;
  operator: ComparisonOperator;
  value: string;
  logicalOperator: LogicalOperator;
}

export const hasFormValues = (
  selectedColumn: HeliosRecordDataTablesColumn | undefined,
  advancedMode: boolean,
  formValues: FilterFormValues,
  isBlank: boolean | null,
  staticValues: string[],
  fromValue: string,
  toValue: string
): boolean => {
  if (!selectedColumn) return false;
  if (isBlank !== null) return true;
  if (advancedMode) return BLANK_OPERATORS.includes(formValues.operator) || !!formValues.value;
  if (selectedColumn.displayType === TemplatePresentationItemDisplay.StaticSelection) return staticValues.length > 0;
  if (isNumericDataType(selectedColumn.dataType) || isDateDataType(selectedColumn.dataType)) return fromValue !== '' || toValue !== '';
  return staticValues.length > 0 || !!formValues.value;
};
